import env from "$root/env";
import { Configuration } from "@eu.mdotm.api/storyfolio-gateway";
import type { AxiosInstance } from "axios";
import { authAxiosInstance } from "./config";

// The Storyfolio Gateway: the next-generation document engine, which renders a design template
// (PPTX today) filled with a portfolio's data. NOT the `storyfolio` functional-area, which edits
// the monolith's Commentary Templates; the two share only a product name.
//
// ## Why this module exists instead of `factory.ts`
//
// 1. A DIFFERENT HOST. `getApiGen` binds every controller to `env.apiOrigin`; the gateway is its
//    own deployment behind its own ingress, with its own `storyfolioApiOrigin`.
// 2. A RELATIVE server url. `storyfolio-gateway.yml` declares `servers: [{ url: /api/v1 }]`, so the
//    generated `BASE_PATH` is the bare string "/api/v1" rather than an origin. Handing it an origin
//    alone drops the version prefix and requests `<origin>/templates` — a 404 that reads like an
//    unpublished template. `storyfolioGatewayBasePath` re-attaches it.
//
// The bearer token is the same one every other request carries: `authAxiosInstance` wires it on,
// and the gateway authenticates it itself and enforces the `storyfolio.generate` scope, answering
// 403 when it is absent. A scope check duplicated here would be a second opinion that can only
// disagree with the authority.

/** The version prefix the generated client's `BASE_PATH` carries, and an origin alone does not. */
const GATEWAY_PATH_PREFIX = "/api/v1";

export function storyfolioGatewayBasePath(origin: string): string {
	return `${origin.replace(/\/+$/, "")}${GATEWAY_PATH_PREFIX}`;
}

/**
 * Whether this environment has a Storyfolio deployment at all.
 *
 * An environment without `storyfolioApiOrigin` is one where the gateway was never deployed, which
 * is a different thing from a customer not being entitled to it — the entitlement check is
 * `hasAccess`. Both have to pass before a template is listed.
 */
export function storyfolioGatewayAvailable(): boolean {
	return Boolean(env.storyfolioApiOrigin);
}

/**
 * The shape both generated gateway factories share. Deliberately NOT `ControllerFactory` from
 * ./factory.ts: that one is typed against the *sphere* client's `Configuration`, and the two are
 * interchangeable only by accident of being generated from the same template.
 */
export type GatewayControllerFactory<T> = (
	configuration?: Configuration,
	basePath?: string,
	axios?: AxiosInstance,
) => T;

/**
 * A Storyfolio Gateway controller bound to this deployment's gateway origin.
 *
 * Throws where the environment publishes no origin, so a caller that skipped
 * {@link storyfolioGatewayAvailable} fails loudly rather than issuing a relative request against
 * the app's own origin.
 */
export function getStoryfolioGatewayApi<T>(
	controllerFactory: GatewayControllerFactory<T>,
	axiosInstance?: AxiosInstance,
): T {
	const origin = env.storyfolioApiOrigin;
	if (!origin) {
		throw new Error("storyfolioApiOrigin is not configured for this environment");
	}
	return controllerFactory(
		new Configuration({ basePath: storyfolioGatewayBasePath(origin) }),
		undefined,
		axiosInstance ?? authAxiosInstance,
	);
}
